/**
 * Risk Management Type Definitions
 * 
 * This file contains TypeScript interfaces for the risk management module
 * Ensures type safety and consistency between frontend and backend
 */

/**
 * Risk source categorization
 */
export enum RiskSource {
  INTERNAL_OPERATIONAL = 'Internal Operational',
  INTERNAL_FINANCIAL = 'Internal Financial',
  EXTERNAL_MARKET = 'External Market',
  EXTERNAL_REGULATORY = 'External Regulatory'
}

/**
 * Risk severity level
 */
export enum RiskLevel {
  CRITICAL = 'Critical',
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low'
}

/**
 * Risk mitigation status
 */
export enum MitigationStatus {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  ONGOING = 'Ongoing Monitoring'
}

/**
 * Risk profile interface matching Supabase database schema
 */
export interface RiskProfile {
  id?: string;
  user_id?: string; // Optional in frontend forms, set by backend
  
  // Risk categorization
  source: RiskSource;
  level: RiskLevel;
  
  // Risk details
  title: string;
  description: string;
  potential_impact: number; // 0-10 scale
  probability_of_occurrence: number; // 0-10 scale
  
  // Risk mitigation
  mitigation_plan?: string;
  mitigation_status?: string;
  mitigation_deadline?: string | null; // ISO string format for dates
  
  // Calculated field (available after DB retrieval)
  risk_score?: number;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

/**
 * Risk analysis summary for dashboard display
 */
export interface RiskSummary {
  total_risks: number;
  critical_risks: number;
  high_risks: number;
  medium_risks: number;
  low_risks: number;
  average_risk_score: number;
  top_risks: RiskProfile[];
}

/**
 * Risk filter options for querying
 */
export interface RiskFilterOptions {
  source?: RiskSource;
  level?: RiskLevel;
  mitigationStatus?: MitigationStatus;
  minRiskScore?: number;
  maxRiskScore?: number;
  sortBy?: 'risk_score' | 'created_at' | 'updated_at' | 'mitigation_deadline';
  sortDirection?: 'asc' | 'desc';
}
